import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiUrl, debugBackendUrl } from '../utils/httpClient';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Check if authentication is disabled via environment variable
  const authDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
  
  const [isAuthenticated, setIsAuthenticated] = useState(authDisabled);
  const [token, setToken] = useState<string | null>(authDisabled ? 'local-dev-token' : null);
  const [loading, setLoading] = useState(!authDisabled);

  useEffect(() => {
    if (authDisabled) {
      // Skip auth checks when disabled
      setIsAuthenticated(true);
      setToken('local-dev-token');
      setLoading(false);
      return;
    }
    
    // Check for stored token on app start
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []); // Run once on mount

  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await fetch(getApiUrl('/auth/verify'), {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
        },
      });

      if (response.ok) {
        setToken(tokenToVerify);
        setIsAuthenticated(true);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('auth_token');
        setToken(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('auth_token');
      setToken(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken: string) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    token,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};